/* eslint-disable import/no-unused-modules */
import React, { ReactElement } from "react"
import { MediaQueryAllQueryable, useMediaQuery } from "react-responsive"
import breakpoints from "@silversea-ssc/design-system-tokens/dist/definitions/breakpoints.json"
import { match } from "ts-pattern"
import { ClientOnly } from "./ClientOnly"

type BreakpointCode = keyof typeof breakpoints

export const VisibleTabletUp: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => (
    <VisibleMediaQueryBreakpoint type="up" from="lg">
        {children}
    </VisibleMediaQueryBreakpoint>
)

export const VisibleMobile: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => (
    <VisibleMediaQueryBreakpoint type="between" from="xs" to="lg">
        {children}
    </VisibleMediaQueryBreakpoint>
)

type VisibleMediaQueryBreakpointProps =
    | {
          type: "up"
          from: BreakpointCode
      }
    | { type: "between"; from: BreakpointCode; to: BreakpointCode }
    | { type: "down"; from: BreakpointCode }
export const VisibleMediaQueryBreakpoint: React.FC<React.PropsWithChildren<VisibleMediaQueryBreakpointProps>> = ({
    children,
    ...props
}) => {
    const query = match<VisibleMediaQueryBreakpointProps, MediaQueryAllQueryable>(props)
        .with({ type: "up" }, ({ from }) => ({
            minWidth: breakpointValue(from),
        }))
        .with({ type: "between" }, ({ from, to }) => betweenBreakpoints(from, to))
        .with({ type: "down" }, ({ from }) => ({ maxWidth: breakpointValue(from) }))
        .exhaustive()

    const matches = useMediaQuery(query)
    return <ClientOnly>{matches ? <>{children}</> : null}</ClientOnly>
}

export const useIsTabletOrDesktop = (): boolean => {
    return useMediaQuery({ minWidth: breakpointValue("lg") })
}

type MediaQueryBreakpoint = {
    up: BreakpointCode
    children: (matches: boolean) => ReactElement
}

export function MediaQueryBreakpoint({ up, children }: MediaQueryBreakpoint): ReactElement {
    const matches = useMediaQuery({ minWidth: breakpointValue(up) })
    return <ClientOnly>{children(matches)}</ClientOnly>
}

const breakpointValue = (code: BreakpointCode): string => breakpoints[code]
const betweenBreakpoints = (from: BreakpointCode, to: BreakpointCode): Partial<MediaQueryAllQueryable> => ({
    minWidth: breakpoints[from],
    maxWidth: pixelValueBeforeBreakpoint(to),
})
const pixelValueBeforeBreakpoint = (breakpoint: BreakpointCode): string =>
    numberToPixelValue(pixelValueToNumber(breakpoints[breakpoint]) - 0.02)
const numberToPixelValue = (value: number): string => `${value}px`
const pixelValueToNumber = (pixelValue: string): number => parseInt(pixelValue, 10)
